import React, { useState, useEffect, useCallback } from 'react';
import {
  Search,
  RefreshCw,
  Building2,
  Activity,
  CheckCircle,
  XCircle,
  Clock,
} from 'lucide-react';
import { dashboardApi } from '../api/dashboardApi';
import './LoginAuditsPage.css';

interface AuditEvent {
  id: string;
  email: string;
  org_name?: string;
  timestamp: string;
  client_ip: string;
  location?: string;
  event_type: string;
  success: boolean;
  failure_reason?: string;
  actor_username?: string;
  actor_role?: string;
  source?: string;
}

interface LicenseSummary {
  email: string;
  event_count: number;
  logins: number;
  logouts: number;
  failures: number;
  latest_timestamp: string;
  latest_event_type: string;
}

interface OrgSummary {
  org_name: string;
  license_count: number;
  total_events: number;
  latest_timestamp: string;
  licenses: LicenseSummary[];
}

interface AuditStats {
  events_today: number;
  total_events: number;
  success_rate: number;
  failed_events: number;
}

const EVENT_LABELS: Record<string, string> = {
  login: 'Client login',
  logout: 'Client logout',
  failed_login: 'Failed login',
  dashboard_login: 'Dashboard login',
  dashboard_logout: 'Dashboard logout',
  new_license: 'New license',
  existing_license: 'License check',
  renewal: 'Renewal',
};

const formatDate = (value?: string) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const eventBadgeClass = (type: string, success: boolean): string => {
  if (!success) return 'au-event-badge au-event-badge--fail';
  if (type.includes('logout')) return 'au-event-badge au-event-badge--logout';
  if (type.includes('login')) return 'au-event-badge au-event-badge--login';
  if (type.startsWith('dashboard')) return 'au-event-badge au-event-badge--admin';
  return 'au-event-badge au-event-badge--license';
};

const LoginAuditsPage: React.FC = () => {
  const [stats, setStats] = useState<AuditStats | null>(null);
  const [organizations, setOrganizations] = useState<OrgSummary[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<string | null>(null);
  const [selectedLicense, setSelectedLicense] = useState<string | null>(null);
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [search, setSearch] = useState('');
  const [eventFilter, setEventFilter] = useState('');
  const [successFilter, setSuccessFilter] = useState<'' | 'true' | 'false'>('');

  const loadSummary = useCallback(async (searchTerm?: string) => {
    try {
      setLoadingSummary(true);
      const [summaryRes, statsRes] = await Promise.all([
        dashboardApi.getAuditSummary(searchTerm || undefined),
        dashboardApi.getAuditStats(),
      ]);
      setOrganizations(summaryRes.organizations || []);
      setStats({
        events_today: statsRes.events_today,
        total_events: statsRes.total_events,
        success_rate: statsRes.success_rate,
        failed_events: statsRes.failed_events,
      });
    } catch (err) {
      console.error('Failed to load audit summary:', err);
    } finally {
      setLoadingSummary(false);
    }
  }, []);

  const loadLicenseEvents = useCallback(
    async (email: string, orgName?: string) => {
      try {
        setLoadingEvents(true);
        const res = await dashboardApi.getAudits({
          email,
          org_name: orgName,
          event_type: eventFilter || undefined,
          success: successFilter === '' ? undefined : successFilter === 'true',
          limit: 200,
          page: 1,
        });
        setEvents(res.audits);
      } catch (err) {
        console.error('Failed to load license events:', err);
        setEvents([]);
      } finally {
        setLoadingEvents(false);
      }
    },
    [eventFilter, successFilter]
  );

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    if (selectedLicense) {
      const org = organizations.find((o) => o.org_name === selectedOrg);
      loadLicenseEvents(selectedLicense, org?.org_name);
    }
  }, [selectedLicense, selectedOrg, organizations, loadLicenseEvents]);

  const handleSearch = () => {
    setSelectedOrg(null);
    setSelectedLicense(null);
    setEvents([]);
    loadSummary(search.trim() || undefined);
  };

  const handleSelectOrg = (orgName: string) => {
    setSelectedOrg(orgName);
    setSelectedLicense(null);
    setEvents([]);
  };

  const handleSelectLicense = (email: string) => {
    setSelectedLicense(email);
  };

  const selectedOrgData = organizations.find((o) => o.org_name === selectedOrg);
  const selectedLicenseData = selectedOrgData?.licenses.find((l) => l.email === selectedLicense);

  return (
    <div className="au-page">
      {stats && (
        <div className="au-stats">
          <div className="au-stat">
            <div className="au-stat-icon au-stat-icon--blue"><Clock size={16} /></div>
            <div>
              <div className="au-stat-value">{stats.events_today}</div>
              <div className="au-stat-label">Today</div>
            </div>
          </div>
          <div className="au-stat">
            <div className="au-stat-icon au-stat-icon--slate"><Activity size={16} /></div>
            <div>
              <div className="au-stat-value">{stats.total_events}</div>
              <div className="au-stat-label">Total events</div>
            </div>
          </div>
          <div className="au-stat">
            <div className="au-stat-icon au-stat-icon--green"><CheckCircle size={16} /></div>
            <div>
              <div className="au-stat-value">{stats.success_rate}%</div>
              <div className="au-stat-label">Success rate</div>
            </div>
          </div>
          <div className="au-stat">
            <div className="au-stat-icon au-stat-icon--red"><XCircle size={16} /></div>
            <div>
              <div className="au-stat-value">{stats.failed_events}</div>
              <div className="au-stat-label">Failed</div>
            </div>
          </div>
        </div>
      )}

      <div className="au-toolbar">
        <input
          className="au-search"
          type="text"
          placeholder="Search organization, license email, or user…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
        />
        <select
          className="au-select"
          value={eventFilter}
          onChange={(e) => setEventFilter(e.target.value)}
        >
          <option value="">All event types</option>
          <option value="login">Client login</option>
          <option value="logout">Client logout</option>
          <option value="failed_login">Failed login</option>
          <option value="dashboard_login">Dashboard login</option>
          <option value="dashboard_logout">Dashboard logout</option>
          <option value="new_license">New license</option>
          <option value="existing_license">License check</option>
          <option value="renewal">Renewal</option>
          <option value="password_change">Password change</option>
        </select>
        <select
          className="au-select"
          value={successFilter}
          onChange={(e) => setSuccessFilter(e.target.value as '' | 'true' | 'false')}
        >
          <option value="">All outcomes</option>
          <option value="true">Successful</option>
          <option value="false">Failed</option>
        </select>
        <button type="button" className="au-btn" onClick={handleSearch}>
          <Search size={14} /> Search
        </button>
        <button type="button" className="au-btn" onClick={() => loadSummary(search.trim() || undefined)}>
          <RefreshCw size={14} className={loadingSummary ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="au-main">
        <div className="au-panel">
          <div className="au-panel-head">
            <h2 className="au-panel-title">Organizations</h2>
            <p className="au-panel-sub">{organizations.length} active org{organizations.length === 1 ? '' : 's'} · click to browse licenses</p>
          </div>
          <div className="au-panel-body">
            {loadingSummary ? (
              <div className="au-loading"><RefreshCw size={18} className="animate-spin" /></div>
            ) : organizations.length === 0 ? (
              <div className="au-loading">No active organizations found</div>
            ) : (
              organizations.map((org) => (
                <button
                  key={org.org_name}
                  type="button"
                  className={`au-org-item ${selectedOrg === org.org_name ? 'active' : ''}`}
                  onClick={() => handleSelectOrg(org.org_name)}
                >
                  <span className="au-org-name">
                    <Building2 size={13} style={{ display: 'inline', marginRight: 4, verticalAlign: -2 }} />
                    {org.org_name}
                  </span>
                  <span className="au-org-meta">
                    {org.license_count} license{org.license_count === 1 ? '' : 's'} · {org.total_events} events
                  </span>
                </button>
              ))
            )}
          </div>
        </div>

        <div className="au-panel">
          {!selectedOrg ? (
            <div className="au-detail-empty">
              <Building2 size={28} style={{ marginBottom: 8, opacity: 0.4 }} />
              Select an organization to view licenses and activity
            </div>
          ) : !selectedLicense ? (
            <>
              <div className="au-detail-header">
                <h3 className="au-detail-org">{selectedOrg}</h3>
                <p className="au-detail-email">{selectedOrgData?.license_count ?? 0} licenses · {selectedOrgData?.total_events ?? 0} total events</p>
              </div>
              <div className="au-panel-body">
                {(selectedOrgData?.licenses || []).map((lic) => (
                  <button
                    key={lic.email}
                    type="button"
                    className="au-license-item"
                    onClick={() => handleSelectLicense(lic.email)}
                  >
                    <span className="au-org-name">{lic.email}</span>
                    <span className="au-org-meta">
                      {lic.event_count} events · {lic.logins} logins · {lic.logouts} logouts
                      {lic.failures > 0 ? ` · ${lic.failures} failed` : ''}
                    </span>
                    <span className="au-org-meta">Last: {formatDate(lic.latest_timestamp)}</span>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="au-detail-header">
                <h3 className="au-detail-org">{selectedOrg}</h3>
                <p className="au-detail-email">{selectedLicense}</p>
                {selectedLicenseData && (
                  <div className="au-detail-stats">
                    <span><strong>{selectedLicenseData.event_count}</strong> events</span>
                    <span><strong>{selectedLicenseData.logins}</strong> logins</span>
                    <span><strong>{selectedLicenseData.logouts}</strong> logouts</span>
                    {selectedLicenseData.failures > 0 && (
                      <span><strong>{selectedLicenseData.failures}</strong> failed</span>
                    )}
                  </div>
                )}
                <button
                  type="button"
                  className="au-btn"
                  style={{ marginTop: 8 }}
                  onClick={() => { setSelectedLicense(null); setEvents([]); }}
                >
                  ← Back to licenses
                </button>
              </div>
              <div className="au-panel-body au-timeline">
                {loadingEvents ? (
                  <div className="au-loading"><RefreshCw size={18} className="animate-spin" /></div>
                ) : events.length === 0 ? (
                  <div className="au-loading">No events for this license</div>
                ) : (
                  events.map((ev) => {
                    const type = ev.event_type || 'unknown';
                    const label = EVENT_LABELS[type] || type.replace(/_/g, ' ');
                    return (
                      <div key={ev.id} className="au-event">
                        <div className={`au-event-dot ${ev.success ? 'au-event-dot--ok' : 'au-event-dot--fail'}`} />
                        <div className="au-event-body">
                          <div className="au-event-top">
                            <span className="au-event-type">{label}</span>
                            <span className={eventBadgeClass(type, ev.success)}>
                              {ev.success ? 'Success' : 'Failed'}
                            </span>
                          </div>
                          <div className="au-event-meta">
                            {formatDate(ev.timestamp)}
                            {ev.actor_username && <> · User: <strong>{ev.actor_username}</strong></>}
                            {ev.actor_role && <> ({ev.actor_role})</>}
                            {ev.client_ip && <> · IP: {ev.client_ip}</>}
                            {ev.location && <> · {ev.location}</>}
                            {!ev.success && ev.failure_reason && <> · {ev.failure_reason}</>}
                            {ev.source && <> · via {ev.source.replace(/_/g, ' ')}</>}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginAuditsPage;
